/**
 * PublicLeagueCoachesPage — /l/:slug/coaches
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P3 (2026-06-09). Manager-CURATED coach
 * profiles (league_coach_profiles) — name, role, team, bio, photo.
 * Nothing here is auto-derived from rosters; the league explicitly
 * chooses who appears (privacy by editorial control).
 */

import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { container, Section, Card, Chip, EmptyState, INK, MUTED, SURFACE } from './pubUi.jsx';

export default function PublicLeagueCoachesPage() {
  const { slug } = useParams();
  const { league, accent } = useOutletContext();
  const [coaches, setCoaches] = useState(null);

  useEffect(() => {
    if (league?.name) document.title = `Coaches — ${league.name}`;
  }, [league?.name]);

  useEffect(() => {
    let cancelled = false;
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/coaches`)
      .then(({ data }) => { if (!cancelled) setCoaches(data?.coaches || []); })
      .catch(() => { if (!cancelled) setCoaches([]); });
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div style={{ ...container, paddingTop: 36 }}>
      <Section eyebrow="Behind the benches" title="Coaches">
        {coaches === null && <div style={{ color: MUTED }}>Loading…</div>}
        {coaches?.length === 0 && (
          <EmptyState icon="👤">Coach profiles haven't been published yet.</EmptyState>
        )}
        {coaches?.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
            {coaches.map(c => (
              <Card key={c.id} style={{ padding: 22 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: c.bio ? 14 : 0 }}>
                  {c.photo_url ? (
                    <img src={c.photo_url} alt=""
                         style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <span style={{
                      width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                      background: SURFACE, display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 24, fontWeight: 900, color: accent,
                    }}>
                      {c.display_name.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 17, color: INK }}>{c.display_name}</div>
                    <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>{c.role}</div>
                    {c.team_id && c.team_name && (
                      <Link to={`/l/${slug}/team/${c.team_id}`} style={{ textDecoration: 'none' }}>
                        <Chip color={accent} bg={`${accent}1a`}>{c.team_name}</Chip>
                      </Link>
                    )}
                  </div>
                </div>
                {c.bio && (
                  <p style={{ margin: 0, fontSize: 14, color: MUTED, lineHeight: 1.6 }}>{c.bio}</p>
                )}
              </Card>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
